import { RPCSchemas } from '@bits/rpc';
import { useToast } from '@bits/ui';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { useFilteredList } from '@/lib/advanced-filters/use-filtered-list';
import { rpcClient } from '@/lib/api/rpc-client';
import { useCustomPropertyByName } from '@/components/custom-properties/use-custom-properties';

const PAGE_SIZE = 20;

export const useFilteredCustomPropertyTypeList = () => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const filteredList = useFilteredList({
    fetchFacets: () =>
      fetchCustomPropertyTypeList({
        filters: [],
        limit: 1,
        offset: 0,
        query: '',
      }).then((r) => ({ facets: r.facets! })),
    savedFiltersKey: 'property-types',
  });

  const {
    isLoading: propertyTypesAreLoading,
    data: propertyTypes,
    mutate: mutatePropertyTypes,
  } = useSWR(
    filteredList.ready &&
      filteredList.formattedRequestFilters && [
        '/custom.properties.types.list',
        filteredList.formattedRequestFilters,
        PAGE_SIZE,
        filteredList.page,
        filteredList.sortBy,
        filteredList.search,
      ],
    () =>
      fetchCustomPropertyTypeList({
        filters: filteredList.formattedRequestFilters!.filter(
          (f): f is NonNullable<typeof f> => f !== undefined
        ),
        limit: PAGE_SIZE,
        offset: (filteredList.page - 1) * PAGE_SIZE,
        query: filteredList.search,
        sorting: [filteredList.sortBy],
      } as RPCSchemas['ListPropertyTypesRequest']),
    {
      shouldRetryOnError: false,
      onError: () => {
        toast({
          variant: 'error',
          title: t('errors:item-list.title'),
          description: t('errors:item-list.description', {
            type: 'property types',
          }),
        });
      },
    }
  );

  return {
    isLoading: propertyTypesAreLoading,
    result: propertyTypes,
    pageSize: PAGE_SIZE,
    ...filteredList,
    mutatePropertyTypes,
  };
};

export const fetchCustomPropertyTypeList = ({
  filters,
  limit,
  offset,
  query,
  sorting,
}: RPCSchemas['ListPropertyTypesRequest']) =>
  rpcClient.call('/custom.properties.types.list', {
    filters,
    limit,
    offset,
    query,
    sorting,
  });

/**
 * This hook is used to get all the property types
 *
 * @returns The property types
 */
export const useAllCustomPropertyTypes = () =>
  useSWR(['custom.properties.types.list'], () =>
    fetchCustomPropertyTypeList({
      filters: [],
      limit: 9999,
      offset: 0,
      sorting: [],
    }).then((r) => r.propertyTypes)
  );

/**
 * This hook is used to get the property type for a property by id
 *
 * @param id - The id of the property type
 * @returns The property type for the property
 */
export const useCustomPropertyTypeById = (id?: string | null) => {
  const request = useAllCustomPropertyTypes();

  return {
    ...request,
    data: request.data?.find((propertyType) => propertyType.id === id),
  };
};

/**
 * This hook is used to get the property type for a property by name
 *
 * @param name - The name of the property
 * @returns The property type for the property
 */
export const useCustomPropertyTypeByName = (name?: string | null) => {
  const request = useAllCustomPropertyTypes();

  return {
    ...request,
    data: request.data?.find((propertyType) => propertyType.name === name),
  };
};

/**
 * This hook is used to get the property type for a property by the property's name
 * Very useful when you don't have the propertyType's id at hand (which is never pretty much)
 *
 * @param name - The name of the property
 * @returns The property type for the property
 */
export const usePropertyTypeForPropertyByName = (name?: string | null) => {
  const customProperty = useCustomPropertyByName(name);
  return useCustomPropertyTypeById(customProperty?.propertyTypeId ?? undefined);
};

export const createCustomPropertyType = (
  request: RPCSchemas['CreateCustomPropertyTypeRequest']
) => rpcClient.call('/custom.properties.types.create', request);

export const updateCustomPropertyType = (
  id: string,
  request: RPCSchemas['CreateCustomPropertyTypeRequest']
) => {
  const { displayNames, type, values } = request;

  return rpcClient.call('/custom.properties.types.update', {
    id,
    displayNames,
    type,
    values,
  });
};

export const archiveCustomPropertyType = (id: string, version: number) =>
  rpcClient.call('/custom.properties.types.archive', {
    id,
    version,
  });

export const unarchiveCustomPropertyType = (id: string, version: number) =>
  rpcClient.call('/custom.properties.types.unarchive', {
    id,
    version,
  });
